import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Sparkles, Loader2, Trash2, ChevronDown } from 'lucide-react';
import { API, pct } from './api';

const TONE = { pending: 'text-amber-300', approved: 'text-emerald-300', rejected: 'text-zinc-500' };

const Card = ({ v, act }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 space-y-1.5" data-testid={`variation-${v.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-white">{v.title} <span className={`text-[10px] uppercase ${TONE[v.status]}`}>{v.status}</span></p>
          <p className="text-[11px] text-zinc-500">{v.label}{v.win_rate != null ? ` · win rate ${pct(v.win_rate)}` : ''}</p>
        </div>
        <div className="ml-auto flex shrink-0 gap-1">
          {v.status !== 'approved' && <button type="button" onClick={() => act(v, 'approve')} data-testid={`variation-approve-${v.id}`} className="inline-flex items-center gap-1 rounded bg-emerald-500 px-2 py-1 text-[11px] font-bold text-black"><Check className="w-3 h-3" />Approve</button>}
          {v.status !== 'rejected' && <button type="button" onClick={() => act(v, 'reject')} data-testid={`variation-reject-${v.id}`} className="inline-flex items-center gap-1 rounded border border-zinc-600 px-2 py-1 text-[11px] text-zinc-300"><X className="w-3 h-3" />{v.status === 'approved' ? 'Retire' : 'Reject'}</button>}
          <button type="button" onClick={() => act(v, 'delete')} data-testid={`variation-delete-${v.id}`} className="rounded px-1.5 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {v.what_changed && <p className="text-xs text-zinc-300"><b className="text-white">What changed:</b> {v.what_changed}</p>}
      {v.why && <p className="text-xs text-zinc-300"><b className="text-white">Why it should win:</b> {v.why}</p>}
      {v.restored_citations?.length > 0 && <p className="text-[11px] text-amber-300">Preset citations put back in: {v.restored_citations.join(', ')}</p>}
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`variation-read-${v.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-300">
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />{open ? 'Hide letter' : 'Read full letter'}
      </button>
      {open && (
        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-2 text-[11px] leading-relaxed text-zinc-300 whitespace-pre-wrap" data-testid={`variation-text-${v.id}`}>
          <b className="text-white">{v.subject}</b>{'\n\n'}{v.pre_body}{'\n\n'}<i className="text-zinc-500">[accounts list]</i>{'\n\n'}{v.post_body}
        </div>
      )}
    </div>
  );
};

// AI-written letter variations: nothing goes live until it's approved here.
export default function Variations() {
  const [data, setData] = useState({ items: [] });
  const [types, setTypes] = useState([]);
  const [type, setType] = useState('regular');
  const [filter, setFilter] = useState('pending');
  const load = useCallback(() => axios.get(`${API}/variations`, { params: { status: filter } }).then((r) => setData(r.data)).catch(() => {}), [filter]);
  useEffect(() => { load(); axios.get(`${API}/letter-types`).then((r) => setTypes(r.data)).catch(() => {}); }, [load]);
  useEffect(() => { if (!data.generating) return undefined; const t = setTimeout(load, 5000); return () => clearTimeout(t); }, [data, load]);
  const act = async (v, a) => {
    try {
      if (a === 'delete') await axios.delete(`${API}/variations/${v.id}`); else await axios.post(`${API}/variations/${v.id}/${a}`);
      toast.success(a === 'approve' ? 'Approved — new letters of this type will use it' : a === 'reject' ? 'Taken out of use' : 'Deleted'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const gen = async () => {
    try { await axios.post(`${API}/variations/generate`, { letter_type: type }); setFilter('pending'); setData((d) => ({ ...d, generating: true })); toast.success('Writing 2 new variations…'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="variations">
      <div className="rounded-xl border border-violet-500/30 bg-zinc-900/70 p-3 space-y-2">
        <p className="text-[11px] text-zinc-400">Every week the AI writes new variations of your most-used letters, based on which ones are getting deletions. Each one keeps that letter's preset language: the subject, every legal citation and every demand. Nothing is used until you approve it, and approved variations that win more get used more.</p>
        <div className="flex flex-wrap gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="variation-type-select" className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-white">
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button type="button" onClick={gen} disabled={data.generating} data-testid="variation-generate"
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-400 disabled:opacity-60">
            {data.generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{data.generating ? 'Writing…' : 'Write new variations now'}
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        {[['pending', 'Waiting for approval'], ['approved', 'Approved (live)'], ['rejected', 'Rejected'], ['', 'All']].map(([k, l]) => (
          <button key={k || 'all'} type="button" onClick={() => setFilter(k)} data-testid={`variation-filter-${k || 'all'}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${filter === k ? 'bg-violet-500 border-violet-500 text-white' : 'border-zinc-700 text-zinc-400'}`}>{l}</button>
        ))}
      </div>
      {!data.items.length && <p className="text-xs text-zinc-500" data-testid="variations-empty">Nothing here yet.</p>}
      {data.items.map((v) => <Card key={v.id} v={v} act={act} />)}
    </div>
  );
}
